import React from "react";
import { observer } from "mobx-react-lite";
import { FTPSearchBar } from "../../../components-new";
import { Button, MenuItem, Select } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useStore } from "../../../helpers";
import { SideOverlay } from "../../../components/side-overlay";
import { useTranslation } from "react-i18next";
import { SrFilter } from "./sr-filter";
import { ModernPagination } from "./modern-pagination";
import ServiceRequestFaceRecognition from "./request-face-recognition";

const PAGE_SIZES = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

type SRSearchPanelProps = {
    onSelectFaceRequest?: (requests: any[]) => void;
};

export const SRSearchPanel = observer(({ onSelectFaceRequest }: SRSearchPanelProps) => {
    const rootStore = useStore();
    const { serviceRequestsStore } = rootStore;

    const { t } = useTranslation("service_request_home");

    const { pageSize, pageNumber, setPageNumber, setPageSize, resultsSize, resultsCount } =
        serviceRequestsStore;

    const handleChange = (event: any) => {
        setPageSize(event.target.value);
        setPageNumber(1);
    };

    const totalPages = Math.ceil(resultsSize / pageSize);

    return (
        <>
            {/* =========================================================
                SEARCH / ACTION BAR
            ========================================================== */}
            <div className="mb-5 rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    {/* Search */}
                    <div className="w-full md:max-w-md">
                        <FTPSearchBar
                            onSearch={(value) => serviceRequestsStore.setSearchQuery(value)}
                            placeHolder={t("by_name")}
                            clientSideSearch={false}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex w-full items-center justify-end gap-3 md:w-auto">
                        {/* Face Recognition */}
                        <ServiceRequestFaceRecognition onSelectRequest={onSelectFaceRequest} />

                        {/* Filters */}
                        <Button
                            variant="outlined"
                            startIcon={<FilterAltIcon />}
                            size="medium"
                            onClick={() => serviceRequestsStore.setServiceFilter(true)}
                            sx={{
                                height: "44px",
                                minWidth: "108px",
                                px: 2,
                                color: "#374151",
                                borderColor: "#D1D5DB",
                                backgroundColor: "#FFFFFF",
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: "9px",
                                boxShadow: "none",
                                "&:hover": {
                                    borderColor: "#9CA3AF",
                                    backgroundColor: "#F9FAFB",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            {t("filters")}
                        </Button>
                    </div>
                </div>
            </div>

            {/* =========================================================
                RESULTS / PAGINATION BAR
            ========================================================== */}
            <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                {/* Results Count */}
                <p className="text-sm text-gray-600">
                    {t("showing")}{" "}
                    <span className="font-semibold text-gray-900">
                        {pageSize < resultsSize ? resultsCount : resultsSize}
                    </span>{" "}
                    {t("records_of")} <span className="font-semibold text-gray-900">{resultsSize}</span>{" "}
                    {t("results")}
                </p>

                {/* Pagination */}
                <div className="flex w-full items-center justify-end gap-4 md:w-auto">
                    {/* Rows */}
                    <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap text-sm font-medium text-gray-600">Rows:</span>

                        <Select
                            value={pageSize}
                            onChange={handleChange}
                            size="small"
                            sx={{
                                minWidth: "72px",
                                height: "40px",
                                borderRadius: "8px",
                                backgroundColor: "#FFFFFF",
                                fontWeight: 500,

                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#D1D5DB",
                                },

                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#9CA3AF",
                                },

                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#3A80F4",
                                    borderWidth: "2px",
                                },
                            }}
                        >
                            {PAGE_SIZES.map((size) => (
                                <MenuItem
                                    value={size}
                                    key={size}
                                    sx={{
                                        fontWeight: 500,

                                        "&.Mui-selected": {
                                            backgroundColor: "#EFF6FF",
                                            color: "#3A80F4",

                                            "&:hover": {
                                                backgroundColor: "#DBEAFE",
                                            },
                                        },
                                    }}
                                >
                                    {size}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    {/* Pagination */}
                    <ModernPagination
                        handlePageChange={(event, page) => {
                            setPageNumber(page);
                        }}
                        pageNumber={pageNumber}
                        totalPages={totalPages}
                    />
                </div>
            </div>

            {/* =========================================================
                FILTER SIDE PANEL
            ========================================================== */}
            <SideOverlay
                children={<SrFilter />}
                onClose={() => {
                    serviceRequestsStore.setServiceFilter(false);
                }}
                show={serviceRequestsStore.showServiceFilters}
                title={t("sf")}
                size="large"
            />
        </>
    );
});
